import { useTranslation } from '@silospot/i18n/react';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, Suspense, lazy, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHead, EstimatedNote, PageHeader } from '../../components/cld';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { usePeriodRange } from '../../stores/period-store';

// The map bundles d3-geo + a world atlas — load it lazily so it never blocks
// the rest of the page (and is excluded from the main chunk).
const WorldMap = lazy(() =>
  import('../../components/cld/worldmap').then((m) => ({ default: m.WorldMap })),
);

function fraudColor(rate: number): string {
  return rate >= 40 ? 'var(--fraud-fg)' : rate >= 20 ? 'var(--suspect-fg)' : 'var(--clean-fg)';
}

function GeoRow({
  label,
  visits,
  suspect,
  max,
  fmt,
}: {
  label: ReactNode;
  visits: number;
  suspect: number;
  max: number;
  fmt: (n: number) => string;
}): ReactElement {
  const rate = visits > 0 ? (suspect / visits) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="w-40 shrink-0 truncate text-[13px] text-[#3a4151]">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-bg">
        <div
          className="h-full rounded-full"
          style={{ width: `${(visits / max) * 100}%`, background: fraudColor(rate) }}
        />
      </div>
      <span className="w-16 shrink-0 text-right font-mono text-[12.5px] text-text-2">
        {fmt(visits)}
      </span>
      <span
        className="w-14 shrink-0 text-right font-mono text-[12px]"
        style={{ color: fraudColor(rate) }}
      >
        {Math.round(rate)} %
      </span>
    </div>
  );
}

export function GeoPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const [selected, setSelected] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['cld-geo', id, range],
    queryFn: () => api.getGeo(id, range),
    enabled: !!id,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const fmt = (n: number) => nf.format(n);
  const header = <PageHeader title={ts('nav.geo')} sub={t('geo.fraudRate')} />;
  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  const maxC = Math.max(1, ...data.countries.map((c) => c.visits));
  const cityList = selected ? data.cities.filter((c) => c.country === selected) : data.cities;
  const maxCity = Math.max(1, ...cityList.map((c) => c.visits), 1);

  return (
    <div>
      {header}
      <Card className="mb-4 p-2">
        <Suspense
          fallback={<div className="h-[460px] animate-pulse rounded-[14px] bg-neutral-bg" />}
        >
          <WorldMap countries={data.countries} selected={selected} onSelect={setSelected} />
        </Suspense>
      </Card>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHead title={t('geo.byCountry')} />
          {data.countries.slice(0, 14).map((c) => (
            <GeoRow
              key={c.country}
              label={
                <span className="inline-flex items-center gap-2">
                  <Flag country={c.country} /> {c.country || '—'}
                </span>
              }
              visits={c.visits}
              suspect={c.suspect}
              max={maxC}
              fmt={fmt}
            />
          ))}
          {data.countries.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-text-3">—</p>
          ) : null}
        </Card>
        <Card>
          <CardHead
            title={t('geo.byCity')}
            right={
              selected ? (
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-[12px] font-semibold text-accent-strong hover:underline"
                >
                  <Flag country={selected} /> {selected} ✕
                </button>
              ) : undefined
            }
          />
          {cityList.slice(0, 14).map((c) => (
            <GeoRow
              key={`${c.city}-${c.country}`}
              label={
                <span className="inline-flex items-center gap-2">
                  <Flag country={c.country} /> {c.city}
                </span>
              }
              visits={c.visits}
              suspect={c.suspect}
              max={maxCity}
              fmt={fmt}
            />
          ))}
          {cityList.length === 0 ? (
            <p className="py-4 text-center text-[13px] text-text-3">—</p>
          ) : null}
        </Card>
      </div>
      <div className="mt-4">
        <EstimatedNote />
      </div>
    </div>
  );
}
